import React, { useEffect, useState } from 'react';
import { PageTitle, RowDivider } from 'styles/Global/commonStyles';
import useToggle from 'hooks/CustomHooks/useToggle';
import { useLoginStore } from 'store/useLoginDataStore';
import { HospitalPageLabel, FilterWrapper, DiagnosisInnerLayout, DiagnosisLayout } from 'styles/Global/hospitalStyles';
import DiagnosisPaginationSearchFilter from '../../../../functions/diagnosisPaginationSearchFilter';
import { diagnosisPaginationSorting } from 'functions/paginationSorting';
import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';
import _ from 'lodash';
import { DiagnosisStatus } from 'utils/statusCode';
import MindUpAdminDiagnosisListPagination from '../MindUpAdminDiagnosisListPage/MindUpAdminDiagnosisListPagination';
import BasicConfirmModal from 'components/Modal/BasicConfirmModal';
import { BorderColor } from 'styles/Global/colorSemantic';
import { useDiagnosisListByStatusQuery } from 'api/Queries/Diagnosis/useDiagnosisListByStatusQuery';
import MindUpAdminAllReservationConsultationFilter from '../MindUpAdminAllReservationConsultationPage/MindUpAdminAllReservationConsultationFilter';
import { IDDiagnosis } from 'models/DCK/basicInterface';
import { addNoShowDisabledField } from '../utils/mindUpAdminFunctions';
import FillOutDialogueSchedule from 'components/DesignSystem/Modal/FillOutDialogueSchedule';
import { useDialogueScheduleHooks } from 'hooks/CustomHooks/useDialogueScheduleHooks';
import useDiagnosisUpdateMutation from 'api/Mutations/Diagnosis/useDiagnosisUpdateMutation';
import { DiagnosisUpdateReqeustType } from 'models/DCK/DCKApiTypes';
import { ReservationType } from '../MindUpAdminDiagnosisListPage/MindUpAdminConsultationListPage';
import { DCKDiagnosisApi } from 'api/DCK/DCKDiagnosisApi';

const MindupAdminUserReservationConsultationPage = () => {
    const { loginData } = useLoginStore();

    const [initData, setInitData] = useState<IDDiagnosis[]>();
    const [filterData, setFilterData] = useState<IDDiagnosis[] | undefined>(initData);
    const [selectedSearchFilter, setselectedSearchFilter] = useState<number>(0);
    const [searchValue, setSearchValue] = useState<string>('');
    const [sortField, setSortField] = useState<string>('');
    const [sortDirections, setSortDirections] = useState<Record<string, boolean>>({ patientName: false, patientIdentityNumber: false, diagnosisDateTime: false });
    const [isShowConfirmModal, setIsShowConfrimModal] = useToggle();
    const [counselData, setCounselData] = useState<ReservationType>();
    const [onSearchFilterDropdown, setOnSearchFilterDropdown] = useToggle();

    const { totalPages, setPage, currentPage, pageGroup, handlePageGroup, currentData } = usePaginationStateHooks(filterData);
    const {
        selectedDate,
        setSelectedDate,
        selectedTime,
        setSelectedTime,
        selectedDialogueScheduleData,
        onDialogueScheduleModal,
        initDate,
        onChangeDiagnosisDateModal,
        closeFillOutDialogueScheduleModal,
        getRqDataUpdateDiagnosisInfo,
    } = useDialogueScheduleHooks();

    // const { data: diagnosisListData, isLoading: diagnosisListLoading, refetch: refetchDiagnosisListData } = useDiagnosisListQuery(paginationMenu);
    const { data: diagnosisListData, isLoading: diagnosisListLoading, refetch: refetchDiagnosisListData } = useDiagnosisListByStatusQuery({ status: [4] });
    const { mutate: diagnosisUpdate } = useDiagnosisUpdateMutation();

    // Data Fetching
    useEffect(() => {
        if (!diagnosisListLoading && diagnosisListData) {
            processDiagnosisData();
            handleResetButton();
        }
    }, [diagnosisListData]);

    // 데이터 처리 함수
    const processDiagnosisData = () => {
        if (!diagnosisListData) return;

        // TODO: 추후 sorting 기능 원복 필요 시 아래 주석 해제(지금 현재 Figma 오름차순 정렬)
        let sortedData = diagnosisListData.filter((data) => data.doctorId === loginData.id).sort((a, b) => a.diagnosisDateTime.localeCompare(b.diagnosisDateTime));
        // .sort((a, b) => b.diagnosisDateTime.localeCompare(a.diagnosisDateTime))
        sortedData = addNoShowDisabledField(sortedData);

        setSortField('diagnosisDateTime');
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            diagnosisDateTime: true,
        }));
        setInitData(sortedData);
        setFilterData(sortedData);
    };

    // handlePeriodFilter Dropdown
    const handleDropdown = (e: React.MouseEvent, idx: number) => {
        e.stopPropagation();
        setOnSearchFilterDropdown();
        setselectedSearchFilter(idx);
        setSearchValue('');
        setPage(1);
    };

    // SearchFilter function
    const handleSearchFilter = () => {
        DiagnosisPaginationSearchFilter(searchValue, setSortDirections, setSortField, selectedSearchFilter, initData, setPage, handlePageGroup, setFilterData, 'mindUpAdmin');
    };

    // reset Button function
    const handleResetButton = () => {
        setSearchValue('');
        setPage(1);
        handlePageGroup(0);
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            diagnosisDateTime: true,
        }));
        setSortField('diagnosisDateTime');
        setCounselData({ diagnosisId: '', reservationDate: '' });
        setselectedSearchFilter(0);
    };

    // sorting function
    const handleSorting = (type: string) => {
        const direction = !sortDirections[type];

        setSortField(type);
        setSortDirections((prev) => ({
            ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
            [type]: direction,
        }));
        setFilterData(diagnosisPaginationSorting(type, filterData || [], direction));
    };

    // 상담 완료 함수
    const setCounselComplete = async () => {
        if (!counselData?.diagnosisId) {
            return;
        }

        const changeStatusRqData = {
            _id: counselData!.diagnosisId,
            status: DiagnosisStatus.counselComplete,
        };

        await DCKDiagnosisApi.changeDiagnosisStatus(changeStatusRqData, loginData.token);
        setIsShowConfrimModal();
        handleResetButton();
        refetchDiagnosisListData();
    };

    const handleDiagnosisUpdate = async () => {
        const rqData = getRqDataUpdateDiagnosisInfo();
        diagnosisUpdate(rqData as DiagnosisUpdateReqeustType, {
            onSuccess: (data) => {
                closeFillOutDialogueScheduleModal();
                refetchDiagnosisListData();
            },
            onError: (error) => {
                console.error('진단 정보 업데이트 실패', error);
            },
        });
    };

    return (
        <DiagnosisLayout>
            {onDialogueScheduleModal && (
                <FillOutDialogueSchedule
                    diagnosisInfo={selectedDialogueScheduleData!}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    initDate={initDate}
                    selectedTime={selectedTime}
                    setSelectedTime={setSelectedTime}
                    onModalClose={closeFillOutDialogueScheduleModal}
                    updateDiagnosisInfoApiCall={handleDiagnosisUpdate}
                    label='상담 일시'
                    title='상담 일시 변경'
                />
            )}
            {isShowConfirmModal && (
                <BasicConfirmModal
                    title='상담을 완료하셨나요?'
                    content='상담을 끝내셨다면, 완료 처리해주세요.'
                    cancelButton={setIsShowConfrimModal}
                    confirmButton={setCounselComplete}
                    firstButton='취소'
                    secondButton='상담 완료'
                />
            )}
            {!currentData || diagnosisListLoading ? null : (
                <DiagnosisInnerLayout>
                    <HospitalPageLabel onClick={() => window.location.reload()}>예약된 나의 상담</HospitalPageLabel>
                    <PageTitle $marginbottom='24px'>예약된 나의 상담</PageTitle>
                    <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                    <FilterWrapper>
                        <MindUpAdminAllReservationConsultationFilter
                            diagnosisNumber={filterData?.length}
                            selectedSearchFilter={selectedSearchFilter}
                            handleDropdown={handleDropdown}
                            setOnSearchFilterDropdown={setOnSearchFilterDropdown}
                            onSearchFilterDropdown={onSearchFilterDropdown}
                            onChange={setSearchValue}
                            searchValue={searchValue}
                            handleSearchFilter={handleSearchFilter}
                        />
                    </FilterWrapper>
                    <MindUpAdminDiagnosisListPagination
                        initNumber={initData?.length}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageGroup={pageGroup}
                        handlePageGroup={handlePageGroup}
                        handlePageChange={setPage}
                        currentData={currentData}
                        handleSorting={handleSorting}
                        sortField={sortField}
                        sortDirections={sortDirections}
                        handleCounselData={setCounselData}
                        handleResetButton={handleResetButton}
                        handleCounselCompleteAlert={setIsShowConfrimModal}
                        onChangeDiagnosisDateModal={onChangeDiagnosisDateModal}
                    />
                </DiagnosisInnerLayout>
            )}
        </DiagnosisLayout>
    );
};

export default MindupAdminUserReservationConsultationPage;
